/**
 * Campaign Section Fallback Component
 * Generic fallback for campaign sections (tournaments, prize-pop, etc.)
 */
import React from 'react';
import { useAppStore } from '../../../../../../store/app/appStore';
import { LOBBY_SECTION_NAME } from '@repo/common';
import TransWithFallback from '../../../../../../shared/components/Translations/TransWrapper';
import styles from './CampaignSectionFallback.module.css';
import { TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns/_exports';

export type CampaignSectionType = 'tournaments' | 'prize-pop' | 'promotions' | 'rebate';

interface CampaignSectionFallbackProps {
  sectionName: string;
  viewMode: string;
  error?: Error;
  onRetry?: () => void;
  /** Type of campaign section for customized messaging */
  sectionType?: CampaignSectionType;
  /** Custom icon emoji */
  icon?: string;
  /** Number of items (for "not found" logic) */
  itemCount?: number;
}

const SECTION_CONFIG: Record<CampaignSectionType, { icon: string; notFoundKey: string; unavailableTitle: string }> = {
  tournaments: {
    icon: '🏆',
    notFoundKey: 'tournaments.not_found',
    unavailableTitle: 'Tournaments Section Unavailable',
  },
  'prize-pop': {
    icon: '🎁',
    notFoundKey: 'campaigns.not_found',
    unavailableTitle: 'Prize Pop Section Unavailable',
  },
  rebate: {
    icon: '💸',
    notFoundKey: 'rebate.not_found',
    unavailableTitle: 'Rebate Section Unavailable',
  },
  promotions: {
    icon: '🎉',
    notFoundKey: 'promotions.not_found',
    unavailableTitle: 'Promotions Section Unavailable',
  },
};

export const CampaignSectionFallback: React.FC<CampaignSectionFallbackProps> = ({
  sectionName,
  viewMode,
  error: _error,
  onRetry,
  sectionType = 'tournaments',
  icon,
  itemCount = 0,
}) => {
  const setSelectedSection = useAppStore((state) => state.setSelectedSection);
  const setViewMode = useAppStore((state) => state.setViewMode);
  const campaignOnly = useAppStore((state) => state.settings.campaignOnly);

  const config = SECTION_CONFIG[sectionType];

  const handleBackToLobby = () => {
    setSelectedSection(LOBBY_SECTION_NAME);
    setViewMode('multi');
  };

  const isSingleView = viewMode === 'single';
  const isSingleItem = itemCount === 1;
  const noItems = itemCount === 0;
  const showNotFoundMessage = isSingleView && (isSingleItem || noItems);

  const displayIcon = icon || config.icon;

  return (
    <div className={styles.fallbackContainer}>
      <div className={styles.errorIcon}>{displayIcon}</div>
      {showNotFoundMessage ? (
        <>
          <h3 className={styles.errorTitle}>
            <TransWithFallback i18nKey={config.notFoundKey} defaults="Item not found" />
          </h3>
          <p className={styles.errorMessage}>
            <TransWithFallback
              i18nKey={`${config.notFoundKey}_description`}
              defaults="The requested item could not be found. It may have expired or been canceled."
            />
          </p>
          <div className={styles.buttonGroup}>
            {isSingleView && campaignOnly?.type !== TOURNAMENTS_CAMPAIGN_TYPE && (
              <button className={styles.button} onClick={handleBackToLobby}>
                <TransWithFallback i18nKey="back_to_lobby" defaults="Back to Lobby" />
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <h3 className={styles.errorTitle}>{config.unavailableTitle}</h3>
          <p className={styles.errorMessage}>
            The {sectionName} section is temporarily unavailable.
            <br />
            Please try refreshing or check back later.
          </p>
          <div className={styles.buttonGroup}>
            {onRetry && (
              <button className={styles.button} onClick={onRetry}>
                <TransWithFallback i18nKey="generic_retry" defaults="Refresh Section" />
              </button>
            )}
            {isSingleView && (
              <button className={styles.button} onClick={handleBackToLobby}>
                <TransWithFallback i18nKey="back_to_lobby" defaults="Back to Lobby" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
